import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import createSelectors from '~/hooks/stores/createSelectors'

/**
 * Unified UI/layout state — the single source of truth for chrome, layout and
 * theme preferences, persisted to one localStorage key. Replaces the older
 * scattered keys (e.g. `nav-menu-expand`) for new shell components.
 *
 * NOTE: the persisted shape (theme, density) is also read by an inline script
 * in `pages/_document.js` to set `data-theme`/`data-density` before hydration
 * and avoid a flash. Keep this key and shape in sync with that script.
 */
export const UI_STORE_LOCAL_STORAGE_KEY = 'strive-ui'

export type ThemeName = 'default' | 'dark' | 'high-contrast'
export type Density = 'comfortable' | 'compact'

export interface UiStore {
  theme: ThemeName
  density: Density
  /** Desktop sidebar collapsed to the icon rail. */
  navCollapsed: boolean
  /** Focus mode: hide header + footer and force the nav to the icon rail. */
  focusMode: boolean

  setTheme: (theme: ThemeName) => void
  setDensity: (density: Density) => void
  setNavCollapsed: (value: boolean) => void
  toggleNavCollapsed: () => void
  setFocusMode: (value: boolean) => void
  toggleFocusMode: () => void
}

const noopStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {}
}

const useUiStoreBase = create<UiStore>()(
  persist(
    immer((set) => ({
      theme: 'default',
      density: 'comfortable',
      navCollapsed: false,
      focusMode: false,

      setTheme: (theme) =>
        set((state) => {
          state.theme = theme
        }),
      setDensity: (density) =>
        set((state) => {
          state.density = density
        }),
      setNavCollapsed: (value) =>
        set((state) => {
          state.navCollapsed = value
        }),
      toggleNavCollapsed: () =>
        set((state) => {
          state.navCollapsed = !state.navCollapsed
        }),
      setFocusMode: (value) =>
        set((state) => {
          state.focusMode = value
        }),
      toggleFocusMode: () =>
        set((state) => {
          state.focusMode = !state.focusMode
        })
    })),
    {
      name: UI_STORE_LOCAL_STORAGE_KEY,
      storage: createJSONStorage(() =>
        globalThis.window === undefined ? noopStorage : globalThis.localStorage
      ),
      // Persist preferences only — never the action functions.
      partialize: (state) => ({
        theme: state.theme,
        density: state.density,
        navCollapsed: state.navCollapsed,
        focusMode: state.focusMode
      })
    }
  )
)

const useUiStore = createSelectors(useUiStoreBase)

export default useUiStore
